import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  MenuItem,
  Chip,
  Alert,
  Snackbar,
  IconButton,
  Tooltip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  InputAdornment,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Block as BlockIcon,
  AccountBalance as BankIcon,
  Assessment as AssessmentIcon,
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { vendorService } from '../services/vendorService';
import { Vendor, VendorStatus, CreateVendorDTO, UpdateVendorDTO, VendorBankDetailsDTO } from '../types/vendor';

const VendorManagement: React.FC = () => {
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [openBankDialog, setOpenBankDialog] = useState(false);
  const [openPerformanceDialog, setOpenPerformanceDialog] = useState(false);
  const [openSuspendDialog, setOpenSuspendDialog] = useState(false);
  const [editingVendor, setEditingVendor] = useState<Vendor | null>(null);
  const [selectedVendor, setSelectedVendor] = useState<Vendor | null>(null);
  const [performanceData, setPerformanceData] = useState<any>(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info' | 'warning',
  });

  // Filters and pagination
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Form data
  const [formData, setFormData] = useState<CreateVendorDTO>({
    name: '',
    phone: '',
    email: '',
    city: '',
    state: '',
    pincode: '',
    address: '',
    aadhaar: '',
    pan: '',
    operatingCities: [],
    territoryAssigned: '',
  });

  const [bankFormData, setBankFormData] = useState<VendorBankDetailsDTO>({
    bankAccountHolder: '',
    bankAccountNumber: '',
    bankIfscCode: '',
    bankName: '',
    bankBranch: '',
    bankProofUrl: '',
  });

  const [suspensionReason, setSuspensionReason] = useState('');

  // Document upload state
  const [aadhaarFile, setAadhaarFile] = useState<File | null>(null);
  const [panFile, setPanFile] = useState<File | null>(null);
  const [aadhaarPreview, setAadhaarPreview] = useState<string | null>(null);
  const [panPreview, setPanPreview] = useState<string | null>(null);
  const [uploadingDocument, setUploadingDocument] = useState(false);

  // Form validation errors
  const [formErrors, setFormErrors] = useState<{
    name?: string;
    phone?: string;
    email?: string;
    pincode?: string;
    aadhaar?: string;
    pan?: string;
  }>({});

  // Bank form validation errors
  const [bankFormErrors, setBankFormErrors] = useState<{
    bankAccountHolder?: string;
    bankAccountNumber?: string;
    bankIfscCode?: string;
    bankName?: string;
  }>({});

  // Validation functions
  const validatePhone = (phone: string): boolean => {
    const phoneRegex = /^[6-9]\d{9}$/;
    return phoneRegex.test(phone);
  };

  const validateEmail = (email: string): boolean => {
    if (!email) return true; // Email is optional
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePincode = (pincode: string): boolean => {
    if (!pincode) return true; // Pincode is optional
    const pincodeRegex = /^\d{6}$/;
    return pincodeRegex.test(pincode);
  };

  const validateAadhaar = (aadhaar: string): boolean => {
    if (!aadhaar) return true; // Aadhaar is optional
    const aadhaarRegex = /^\d{12}$/;
    return aadhaarRegex.test(aadhaar);
  };

  const validatePAN = (pan: string): boolean => {
    if (!pan) return true; // PAN is optional
    const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
    return panRegex.test(pan.toUpperCase());
  };

  const validateIFSC = (ifsc: string): boolean => {
    const ifscRegex = /^[A-Z]{4}0[A-Z0-9]{6}$/;
    return ifscRegex.test(ifsc.toUpperCase());
  };

  const validateAccountNumber = (accountNumber: string): boolean => {
    const accountRegex = /^\d{9,18}$/;
    return accountRegex.test(accountNumber);
  };

  const validateForm = (): boolean => {
    const errors: typeof formErrors = {};

    // Name validation
    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    } else if (formData.name.trim().length < 3) {
      errors.name = 'Name must be at least 3 characters';
    } else if (formData.name.trim().length > 100) {
      errors.name = 'Name must not exceed 100 characters';
    }

    // Phone validation
    if (!formData.phone.trim()) {
      errors.phone = 'Phone number is required';
    } else if (!validatePhone(formData.phone)) {
      errors.phone = 'Invalid phone number (10 digits, starting with 6-9)';
    }

    // Email validation
    if (formData.email && !validateEmail(formData.email)) {
      errors.email = 'Invalid email format';
    }

    // Pincode validation
    if (formData.pincode && !validatePincode(formData.pincode)) {
      errors.pincode = 'Invalid pincode (must be 6 digits)';
    }

    // Aadhaar validation
    if (formData.aadhaar && !validateAadhaar(formData.aadhaar)) {
      errors.aadhaar = 'Invalid Aadhaar number (must be 12 digits)';
    }

    // PAN validation
    if (formData.pan && !validatePAN(formData.pan)) {
      errors.pan = 'Invalid PAN format (e.g., ABCDE1234F)';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateBankForm = (): boolean => {
    const errors: typeof bankFormErrors = {};

    // Account holder name validation
    if (!bankFormData.bankAccountHolder.trim()) {
      errors.bankAccountHolder = 'Account holder name is required';
    } else if (bankFormData.bankAccountHolder.trim().length < 3) {
      errors.bankAccountHolder = 'Name must be at least 3 characters';
    }

    // Account number validation
    if (!bankFormData.bankAccountNumber.trim()) {
      errors.bankAccountNumber = 'Account number is required';
    } else if (!validateAccountNumber(bankFormData.bankAccountNumber)) {
      errors.bankAccountNumber = 'Invalid account number (9-18 digits)';
    }

    // IFSC code validation
    if (!bankFormData.bankIfscCode.trim()) {
      errors.bankIfscCode = 'IFSC code is required';
    } else if (!validateIFSC(bankFormData.bankIfscCode)) {
      errors.bankIfscCode = 'Invalid IFSC format (e.g., SBIN0001234)';
    }

    // Bank name validation
    if (!bankFormData.bankName.trim()) {
      errors.bankName = 'Bank name is required';
    } else if (bankFormData.bankName.trim().length < 3) {
      errors.bankName = 'Bank name must be at least 3 characters';
    }

    setBankFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const loadVendors = useCallback(async (showLoader = true) => {
    try {
      if (showLoader) setLoading(true);

      console.log('📡 Loading vendors...', {
        page: page + 1,
        limit: rowsPerPage,
        filters: { search: searchTerm, status: statusFilter, city: cityFilter },
      });

      const response = await vendorService.getVendors({
        page: page + 1,
        limit: rowsPerPage,
        search: searchTerm || undefined,
        status: statusFilter || undefined,
        city: cityFilter || undefined,
      });

      console.log('✅ Vendors loaded:', response);

      setVendors(response.data || []);
      setTotalCount(response.pagination?.total || 0);
    } catch (error) {
      console.error('❌ Error loading vendors:', error);
      setSnackbar({
        open: true,
        message: `Failed to load vendors: ${(error as Error).message}`,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchTerm, statusFilter, cityFilter]);

  useEffect(() => {
    loadVendors();
  }, [loadVendors]);

  const handleOpenDialog = (vendor?: Vendor) => {
    if (vendor) {
      setEditingVendor(vendor);
      setFormData({
        name: vendor.name,
        phone: vendor.phone,
        email: vendor.email || '',
        city: vendor.city || '',
        state: vendor.state || '',
        pincode: vendor.pincode || '',
        address: vendor.address || '',
        aadhaar: vendor.aadhaar || '',
        pan: vendor.pan || '',
        operatingCities: vendor.operatingCities || [],
        territoryAssigned: vendor.territoryAssigned || '',
      });
      // Set existing document previews if URLs exist
      if (vendor.aadhaarUrl) {
        setAadhaarPreview(vendor.aadhaarUrl);
      }
      if (vendor.panUrl) {
        setPanPreview(vendor.panUrl);
      }
    } else {
      setEditingVendor(null);
      setFormData({
        name: '',
        phone: '',
        email: '',
        city: '',
        state: '',
        pincode: '',
        address: '',
        aadhaar: '',
        pan: '',
        operatingCities: [],
        territoryAssigned: '',
      });
      setAadhaarFile(null);
      setPanFile(null);
      setAadhaarPreview(null);
      setPanPreview(null);
    }
    setFormErrors({});
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingVendor(null);
    setFormErrors({});
    setAadhaarFile(null);
    setPanFile(null);
    setAadhaarPreview(null);
    setPanPreview(null);
  };

  // File upload handlers
  const handleFileSelect = (
    event: React.ChangeEvent<HTMLInputElement>,
    documentType: 'aadhaar' | 'pan'
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
    if (!validTypes.includes(file.type)) {
      setSnackbar({
        open: true,
        message: 'Please upload only JPG, PNG, or PDF files',
        severity: 'error',
      });
      return;
    }

    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      setSnackbar({
        open: true,
        message: 'File size must be less than 10MB',
        severity: 'error',
      });
      return;
    }

    // Set file and create preview
    if (documentType === 'aadhaar') {
      setAadhaarFile(file);
      // Create preview for images
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setAadhaarPreview(reader.result as string);
        };
        reader.readAsDataURL(file);
      } else {
        setAadhaarPreview('PDF Document');
      }
    } else {
      setPanFile(file);
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setPanPreview(reader.result as string);
        };
        reader.readAsDataURL(file);
      } else {
        setPanPreview('PDF Document');
      }
    }
  };

  const handleRemoveFile = (documentType: 'aadhaar' | 'pan') => {
    if (documentType === 'aadhaar') {
      setAadhaarFile(null);
      setAadhaarPreview(null);
    } else {
      setPanFile(null);
      setPanPreview(null);
    }
  };

  const uploadDocuments = async (vendorId: string) => {
    const uploads: Promise<any>[] = [];

    if (aadhaarFile) {
      uploads.push(
        vendorService.uploadDocument(vendorId, aadhaarFile, 'aadhaar', 'admin')
      );
    }

    if (panFile) {
      uploads.push(
        vendorService.uploadDocument(vendorId, panFile, 'pan', 'admin')
      );
    }

    if (uploads.length > 0) {
      setUploadingDocument(true);
      try {
        await Promise.all(uploads);
        setSnackbar({
          open: true,
          message: 'Documents uploaded successfully',
          severity: 'success',
        });
      } catch (error) {
        console.error('❌ Error uploading documents:', error);
        setSnackbar({
          open: true,
          message: `Failed to upload documents: ${(error as Error).message}`,
          severity: 'error',
        });
        throw error;
      } finally {
        setUploadingDocument(false);
      }
    }
  };

  const handleSaveVendor = async () => {
    // Validate form
    if (!validateForm()) {
      setSnackbar({
        open: true,
        message: 'Please fix the validation errors',
        severity: 'error',
      });
      return;
    }

    try {
      setLoading(true);
      let vendorId: string;

      if (editingVendor) {
        await vendorService.updateVendor(editingVendor.id, formData as UpdateVendorDTO);
        vendorId = editingVendor.id;

        // Upload documents if any
        if (aadhaarFile || panFile) {
          await uploadDocuments(vendorId);
        }

        setSnackbar({
          open: true,
          message: 'Vendor updated successfully',
          severity: 'success',
        });
      } else {
        const response = await vendorService.createVendor(formData);
        vendorId = response.data.id;

        // Upload documents if any
        if (aadhaarFile || panFile) {
          await uploadDocuments(vendorId);
        }

        setSnackbar({
          open: true,
          message: 'Vendor created successfully',
          severity: 'success',
        });
      }
      handleCloseDialog();
      loadVendors(false);
    } catch (error) {
      console.error('❌ Error saving vendor:', error);
      setSnackbar({
        open: true,
        message: `Failed to save vendor: ${(error as Error).message}`,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyVendor = async (vendor: Vendor) => {
    try {
      const userId = localStorage.getItem('userId') || 'admin';
      await vendorService.verifyVendor(vendor.id, userId);
      setSnackbar({
        open: true,
        message: 'Vendor verified successfully',
        severity: 'success',
      });
      loadVendors(false);
    } catch (error) {
      console.error('❌ Error verifying vendor:', error);
      setSnackbar({
        open: true,
        message: `Failed to verify vendor: ${(error as Error).message}`,
        severity: 'error',
      });
    }
  };

  const handleOpenSuspendDialog = (vendor: Vendor) => {
    setSelectedVendor(vendor);
    setSuspensionReason('');
    setOpenSuspendDialog(true);
  };

  const handleSuspendVendor = async () => {
    if (!selectedVendor || !suspensionReason.trim()) {
      setSnackbar({
        open: true,
        message: 'Please provide a suspension reason',
        severity: 'warning',
      });
      return;
    }

    try {
      const userId = localStorage.getItem('userId') || 'admin';
      await vendorService.suspendVendor(selectedVendor.id, suspensionReason, userId);
      setSnackbar({
        open: true,
        message: 'Vendor suspended successfully',
        severity: 'success',
      });
      setOpenSuspendDialog(false);
      loadVendors(false);
    } catch (error) {
      console.error('❌ Error suspending vendor:', error);
      setSnackbar({
        open: true,
        message: `Failed to suspend vendor: ${(error as Error).message}`,
        severity: 'error',
      });
    }
  };

  const handleActivateVendor = async (vendor: Vendor) => {
    try {
      await vendorService.activateVendor(vendor.id);
      setSnackbar({
        open: true,
        message: 'Vendor activated successfully',
        severity: 'success',
      });
      loadVendors(false);
    } catch (error) {
      console.error('❌ Error activating vendor:', error);
      setSnackbar({
        open: true,
        message: `Failed to activate vendor: ${(error as Error).message}`,
        severity: 'error',
      });
    }
  };

  const handleOpenBankDialog = (vendor: Vendor) => {
    setSelectedVendor(vendor);
    setBankFormData({
      bankAccountHolder: vendor.bankAccountHolder || '',
      bankAccountNumber: vendor.bankAccountNumber || '',
      bankIfscCode: vendor.bankIfscCode || '',
      bankName: vendor.bankName || '',
      bankBranch: vendor.bankBranch || '',
      bankProofUrl: vendor.bankProofUrl || '',
    });
    setBankFormErrors({});
    setOpenBankDialog(true);
  };

  const handleSaveBankDetails = async () => {
    if (!selectedVendor) return;

    // Validate bank form
    if (!validateBankForm()) {
      setSnackbar({
        open: true,
        message: 'Please fix the validation errors',
        severity: 'error',
      });
      return;
    }

    try {
      await vendorService.updateBankDetails(selectedVendor.id, bankFormData);
      setSnackbar({
        open: true,
        message: 'Bank details updated successfully',
        severity: 'success',
      });
      setOpenBankDialog(false);
      loadVendors(false);
    } catch (error) {
      console.error('❌ Error updating bank details:', error);
      setSnackbar({
        open: true,
        message: `Failed to update bank details: ${(error as Error).message}`,
        severity: 'error',
      });
    }
  };

  const handleViewPerformance = async (vendor: Vendor) => {
    try {
      setSelectedVendor(vendor);
      setOpenPerformanceDialog(true);
      const performance = await vendorService.getVendorPerformance(vendor.id);
      setPerformanceData(performance.data);
    } catch (error) {
      console.error('❌ Error loading performance:', error);
      setSnackbar({
        open: true,
        message: `Failed to load performance: ${(error as Error).message}`,
        severity: 'error',
      });
    }
  };

  const getStatusColor = (status: VendorStatus) => {
    switch (status) {
      case VendorStatus.VERIFIED:
        return 'success';
      case VendorStatus.ACTIVE:
        return 'primary';
      case VendorStatus.SUSPENDED:
        return 'error';
      case VendorStatus.PENDING:
      default:
        return 'warning';
    }
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1">
          Vendor Management
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => loadVendors()}
          >
            Refresh
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
            Add Vendor
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                placeholder="Search by name, phone, or code"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value={VendorStatus.PENDING}>Pending</MenuItem>
                  <MenuItem value={VendorStatus.VERIFIED}>Verified</MenuItem>
                  <MenuItem value={VendorStatus.ACTIVE}>Active</MenuItem>
                  <MenuItem value={VendorStatus.SUSPENDED}>Suspended</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="City"
                value={cityFilter}
                onChange={(e) => setCityFilter(e.target.value)}
                placeholder="Filter by city"
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter('');
                  setCityFilter('');
                }}
                sx={{ height: '56px' }}
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Vendor Code</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>City</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Referrals</TableCell>
                <TableCell align="right">Conversion Rate</TableCell>
                <TableCell align="right">Commission Earned</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : vendors.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Typography variant="body2" color="textSecondary">
                      No vendors found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                vendors.map((vendor) => (
                  <TableRow key={vendor.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {vendor.vendorCode}
                      </Typography>
                    </TableCell>
                    <TableCell>{vendor.name}</TableCell>
                    <TableCell>{vendor.phone}</TableCell>
                    <TableCell>{vendor.city || '-'}</TableCell>
                    <TableCell>
                      <Chip
                        label={vendor.status}
                        color={getStatusColor(vendor.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">{vendor.totalReferrals}</TableCell>
                    <TableCell align="right">{(vendor.conversionRate * 100).toFixed(1)}%</TableCell>
                    <TableCell align="right">₹{vendor.totalCommissionEarned.toFixed(2)}</TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={1} justifyContent="center">
                        <Tooltip title="View Performance">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleViewPerformance(vendor)}
                          >
                            <AssessmentIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleOpenDialog(vendor)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Bank Details">
                          <IconButton
                            size="small"
                            color="info"
                            onClick={() => handleOpenBankDialog(vendor)}
                          >
                            <BankIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {vendor.status === VendorStatus.PENDING && (
                          <Tooltip title="Verify">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleVerifyVendor(vendor)}
                            >
                              <CheckCircleIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {vendor.status !== VendorStatus.SUSPENDED && (
                          <Tooltip title="Suspend">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleOpenSuspendDialog(vendor)}
                            >
                              <BlockIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {vendor.status === VendorStatus.SUSPENDED && (
                          <Tooltip title="Activate">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleActivateVendor(vendor)}
                            >
                              <CheckCircleIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Paper>

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editingVendor ? 'Edit Vendor' : 'Add New Vendor'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Name"
                value={formData.name}
                onChange={(e) => {
                  setFormData({ ...formData, name: e.target.value });
                  if (formErrors.name) {
                    setFormErrors({ ...formErrors, name: undefined });
                  }
                }}
                error={!!formErrors.name}
                helperText={formErrors.name || 'Vendor full name (min 3 characters)'}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Phone"
                value={formData.phone}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                  setFormData({ ...formData, phone: value });
                  if (formErrors.phone) {
                    setFormErrors({ ...formErrors, phone: undefined });
                  }
                }}
                error={!!formErrors.phone}
                helperText={formErrors.phone || '10 digit mobile number'}
                inputProps={{ maxLength: 10 }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => {
                  setFormData({ ...formData, email: e.target.value });
                  if (formErrors.email) {
                    setFormErrors({ ...formErrors, email: undefined });
                  }
                }}
                error={!!formErrors.email}
                helperText={formErrors.email || 'Optional: vendor email address'}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="City"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                helperText="Vendor's city"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="State"
                value={formData.state}
                onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                helperText="Vendor's state"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Pincode"
                value={formData.pincode}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setFormData({ ...formData, pincode: value });
                  if (formErrors.pincode) {
                    setFormErrors({ ...formErrors, pincode: undefined });
                  }
                }}
                error={!!formErrors.pincode}
                helperText={formErrors.pincode || '6 digit pincode'}
                inputProps={{ maxLength: 6 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                multiline
                rows={2}
                helperText="Complete address"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Aadhaar Number"
                value={formData.aadhaar}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 12);
                  setFormData({ ...formData, aadhaar: value });
                  if (formErrors.aadhaar) {
                    setFormErrors({ ...formErrors, aadhaar: undefined });
                  }
                }}
                error={!!formErrors.aadhaar}
                helperText={formErrors.aadhaar || 'Optional: 12 digit Aadhaar number'}
                inputProps={{ maxLength: 12 }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="PAN Number"
                value={formData.pan}
                onChange={(e) => {
                  const value = e.target.value.toUpperCase().slice(0, 10);
                  setFormData({ ...formData, pan: value });
                  if (formErrors.pan) {
                    setFormErrors({ ...formErrors, pan: undefined });
                  }
                }}
                error={!!formErrors.pan}
                helperText={formErrors.pan || 'Optional: PAN in format ABCDE1234F'}
                inputProps={{ maxLength: 10, style: { textTransform: 'uppercase' } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Territory Assigned"
                value={formData.territoryAssigned}
                onChange={(e) => setFormData({ ...formData, territoryAssigned: e.target.value })}
                helperText="Geographic territory or region assigned"
              />
            </Grid>

            {/* Document Upload Section */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2, mb: 1 }}>
                Document Uploads (Optional)
              </Typography>
            </Grid>

            {/* Aadhaar Upload */}
            <Grid item xs={12} md={6}>
              <Box>
                <Typography variant="body2" gutterBottom>
                  Aadhaar Card
                </Typography>
                <input
                  accept="image/*,.pdf"
                  style={{ display: 'none' }}
                  id="aadhaar-upload"
                  type="file"
                  onChange={(e) => handleFileSelect(e, 'aadhaar')}
                />
                <label htmlFor="aadhaar-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<CloudUploadIcon />}
                    fullWidth
                    disabled={uploadingDocument}
                  >
                    Upload Aadhaar
                  </Button>
                </label>
                {aadhaarPreview && (
                  <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    {aadhaarPreview === 'PDF Document' ? (
                      <Chip label="PDF Uploaded" size="small" color="success" />
                    ) : (
                      <Box
                        component="img"
                        src={aadhaarPreview}
                        alt="Aadhaar preview"
                        sx={{
                          maxWidth: '100%',
                          maxHeight: 100,
                          objectFit: 'contain',
                          borderRadius: 1,
                          border: '1px solid #e0e0e0',
                        }}
                      />
                    )}
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      {aadhaarPreview.startsWith('http') && (
                        <IconButton
                          size="small"
                          onClick={() => window.open(aadhaarPreview, '_blank')}
                          title="View document"
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      )}
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveFile('aadhaar')}
                        title="Remove document"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                )}
              </Box>
            </Grid>

            {/* PAN Upload */}
            <Grid item xs={12} md={6}>
              <Box>
                <Typography variant="body2" gutterBottom>
                  PAN Card
                </Typography>
                <input
                  accept="image/*,.pdf"
                  style={{ display: 'none' }}
                  id="pan-upload"
                  type="file"
                  onChange={(e) => handleFileSelect(e, 'pan')}
                />
                <label htmlFor="pan-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<CloudUploadIcon />}
                    fullWidth
                    disabled={uploadingDocument}
                  >
                    Upload PAN
                  </Button>
                </label>
                {panPreview && (
                  <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    {panPreview === 'PDF Document' ? (
                      <Chip label="PDF Uploaded" size="small" color="success" />
                    ) : (
                      <Box
                        component="img"
                        src={panPreview}
                        alt="PAN preview"
                        sx={{
                          maxWidth: '100%',
                          maxHeight: 100,
                          objectFit: 'contain',
                          borderRadius: 1,
                          border: '1px solid #e0e0e0',
                        }}
                      />
                    )}
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      {panPreview.startsWith('http') && (
                        <IconButton
                          size="small"
                          onClick={() => window.open(panPreview, '_blank')}
                          title="View document"
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      )}
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveFile('pan')}
                        title="Remove document"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                )}
              </Box>
            </Grid>

            {uploadingDocument && (
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="text.secondary">
                    Uploading documents...
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSaveVendor} variant="contained" disabled={loading || uploadingDocument}>
            {editingVendor ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bank Details Dialog */}
      <Dialog open={openBankDialog} onClose={() => setOpenBankDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Bank Details - {selectedVendor?.name}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Account Holder Name"
                value={bankFormData.bankAccountHolder}
                onChange={(e) => {
                  setBankFormData({ ...bankFormData, bankAccountHolder: e.target.value });
                  if (bankFormErrors.bankAccountHolder) {
                    setBankFormErrors({ ...bankFormErrors, bankAccountHolder: undefined });
                  }
                }}
                error={!!bankFormErrors.bankAccountHolder}
                helperText={bankFormErrors.bankAccountHolder || 'Full name as per bank account'}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Account Number"
                value={bankFormData.bankAccountNumber}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 18);
                  setBankFormData({ ...bankFormData, bankAccountNumber: value });
                  if (bankFormErrors.bankAccountNumber) {
                    setBankFormErrors({ ...bankFormErrors, bankAccountNumber: undefined });
                  }
                }}
                error={!!bankFormErrors.bankAccountNumber}
                helperText={bankFormErrors.bankAccountNumber || '9-18 digit account number'}
                inputProps={{ maxLength: 18 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="IFSC Code"
                value={bankFormData.bankIfscCode}
                onChange={(e) => {
                  const value = e.target.value.toUpperCase().slice(0, 11);
                  setBankFormData({ ...bankFormData, bankIfscCode: value });
                  if (bankFormErrors.bankIfscCode) {
                    setBankFormErrors({ ...bankFormErrors, bankIfscCode: undefined });
                  }
                }}
                error={!!bankFormErrors.bankIfscCode}
                helperText={bankFormErrors.bankIfscCode || 'IFSC code (e.g., SBIN0001234)'}
                inputProps={{ maxLength: 11, style: { textTransform: 'uppercase' } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Bank Name"
                value={bankFormData.bankName}
                onChange={(e) => {
                  setBankFormData({ ...bankFormData, bankName: e.target.value });
                  if (bankFormErrors.bankName) {
                    setBankFormErrors({ ...bankFormErrors, bankName: undefined });
                  }
                }}
                error={!!bankFormErrors.bankName}
                helperText={bankFormErrors.bankName || 'Name of the bank'}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Branch"
                value={bankFormData.bankBranch}
                onChange={(e) => setBankFormData({ ...bankFormData, bankBranch: e.target.value })}
                helperText="Optional: Bank branch name"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Bank Proof URL"
                value={bankFormData.bankProofUrl}
                onChange={(e) =>
                  setBankFormData({ ...bankFormData, bankProofUrl: e.target.value })
                }
                placeholder="https://..."
                helperText="Optional: URL to cancelled cheque or passbook"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenBankDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveBankDetails} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Suspend Dialog */}
      <Dialog open={openSuspendDialog} onClose={() => setOpenSuspendDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Suspend Vendor - {selectedVendor?.name}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            required
            label="Suspension Reason"
            value={suspensionReason}
            onChange={(e) => setSuspensionReason(e.target.value)}
            multiline
            rows={4}
            sx={{ mt: 2 }}
            placeholder="Provide a detailed reason for suspension"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenSuspendDialog(false)}>Cancel</Button>
          <Button onClick={handleSuspendVendor} variant="contained" color="error">
            Suspend
          </Button>
        </DialogActions>
      </Dialog>

      {/* Performance Dialog */}
      <Dialog
        open={openPerformanceDialog}
        onClose={() => setOpenPerformanceDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Performance - {selectedVendor?.name}</DialogTitle>
        <DialogContent>
          {performanceData ? (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Typography variant="h6">Total Referrals</Typography>
                    <Typography variant="h4">{performanceData.totalReferrals}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Typography variant="h6">Conversion Rate</Typography>
                    <Typography variant="h4">
                      {(performanceData.conversionRate * 100).toFixed(1)}%
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Typography variant="h6">Total Earned</Typography>
                    <Typography variant="h4">₹{performanceData.totalCommissionEarned}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Conversions by Stage
                    </Typography>
                    <Stack spacing={1}>
                      <Box display="flex" justifyContent="space-between">
                        <Typography>Registered:</Typography>
                        <Typography fontWeight="bold">
                          {performanceData.conversionsByStage?.registered || 0}
                        </Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <Typography>KYC Verified:</Typography>
                        <Typography fontWeight="bold">
                          {performanceData.conversionsByStage?.kycVerified || 0}
                        </Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <Typography>First Delivery:</Typography>
                        <Typography fontWeight="bold">
                          {performanceData.conversionsByStage?.firstDelivery || 0}
                        </Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <Typography>Retained:</Typography>
                        <Typography fontWeight="bold">
                          {performanceData.conversionsByStage?.retained || 0}
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          ) : (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPerformanceDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default VendorManagement;
